<template>
  <div class="seat-map-navigation">
    <div
      class="seat-map-navigation seat-map-navigation--desktop"
      role="tablist"
    >
      <seat-navigation-accordion-block
        v-for="segment in segments"
        :key="segment.segmentId"
        :segment="segment"
        :everyoneSeated="isEveryoneSeated(segment.segmentId)"
        :isOpen="segment.segmentId === currentSegmentId"
        :onOpen="
          () => {
            if (segment.segmentId !== currentSegmentId) {
              setCurrentSegmentId(segment.segmentId);
            }
          }
        "
      >
        <template v-slot:flight>
          <div class="segment-route pr-2">
            <div
              v-if="segment_city_route(segment)"
              class="segment-route__cities"
            >
              {{ segment_city_route(segment) }}
            </div>
            <div class="segment-info d-flex">
              <img
                class="carrier-logo"
                :alt="(segment.carrier || {}).code"
                :src="`https://static.vadobay.com/airlines-logos/200x100/${(segment.carrier || {}).code}.png`"
              />
              <span v-if="(segment.aircraft || {}).code">
                {{ segment.aircraft.code }}
              </span>
              <span v-if="(segment.carrier || {}).name">
                ({{ segment.carrier.name }})
              </span>
              <span v-else>
                ({{ (segment.carrier || {}).code }})
              </span>
            </div>
          </div>
        </template>
        <template v-slot:deck v-if="availableDecks.length > 1">
          <selector
            :disabled="disabled"
            label="Selezione deck"
            :selected="currentDeckIndex"
            :options="availableDecks"
            :onChange="setCurrentDeckIndex"
            showProgress
          >
            <template v-slot:inside>
              <div class="segment-info thin d-flex">
                <span>{{ currentDeckIndex }}</span>
              </div>
            </template>
          </selector>
        </template>
        <template v-slot:passengers>
          <div class="selected-seats d-flex w-100">
            <div
              class="m-0 p-0 w-100 d-flex justify-content-between align-items-center"
            >
              <div class="passenger-selection-title semibold">
                Selezione del passeggero
              </div>
              <div class="passenger-cancel-col">
                <vb-button
                  v-if="arePassengersSeatedOnSegment(segment.segmentId)"
                  class="m-0 p-0 border-0 small"
                  :vb_options="{ variant: 'ghost' }"
                  role="button"
                  aria-label="Cancella"
                  @click="
                    () => {
                      resetSegmentSelections(segment.segmentId);
                      if (sittingPassengers.length) {
                        setCurrentPassengerId(sittingPassengers[0].id);
                      }
                    }
                  "
                >
                  <vb-icon name="close" :size="16" />
                </vb-button>
              </div>
            </div>
            <passenger-selection-row
              v-for="passenger in sittingPassengers"
              :key="passenger.id"
              :passenger="passenger"
              :seat="getPassengerSeat(segment.segmentId, passenger.id)"
              :selected="currentPassengerId === passenger.id"
              :onClick="() => setCurrentPassengerId(passenger.id)"
            />
          </div>
        </template>
      </seat-navigation-accordion-block>
    </div>
  </div>
</template>

<script>
import {
  are_passengers_seated_on_segment,
  get_passenger_seat,
  is_everyone_seated,
} from "@liveflight/utilities/seat-map";
import Selector from "@liveflight/components/blocks/selector.vue";
import PassengerSelectionRow from "@liveflight/components/blocks/seat-map/navigation/passenger-selection-row.vue";
import SeatNavigationAccordionBlock from "./seat-navigation-accordion-block.vue";

export default {
  name: "seat-map-navigation",
  components: {
    selector: Selector,
    "passenger-selection-row": PassengerSelectionRow,
    "seat-navigation-accordion-block": SeatNavigationAccordionBlock,
  },
  props: {
    disabled: Boolean,

    segments: Array,
    locations: { type: Object, default: () => ({}) },
    currentSegmentIndex: Number,
    currentSegmentId: String,
    setCurrentSegmentId: Function,

    availableDecks: Array,
    currentDeckIndex: String,
    setCurrentDeckIndex: Function,

    sittingPassengers: Array,
    currentPassengerId: String,
    setCurrentPassengerId: Function,

    parsedSelections: Object,
    resetSegmentSelections: Function,
  },
  computed: {
    currentCarrier() {
      try {
        if (this.segments.length && this.segments[this.currentSegmentIndex])
          return this.segments[this.currentSegmentIndex].carrier;
      } catch {
        return {};
      }
      return {};
    },
    aircraftCode() {
      if (this.segments.length && this.segments[this.currentSegmentIndex]) {
        const { aircraft = {} } = this.segments[this.currentSegmentIndex];
        return aircraft.code;
      }
      return "";
    },
  },
  methods: {
    endpoint_city(endpoint) {
      if (!endpoint) return "";
      const entry = this.locations[endpoint.iataCode] || {};
      return (
        entry.cityName ||
        endpoint.cityName ||
        entry.name ||
        endpoint.airportName ||
        ""
      );
    },
    segment_city_route(segment) {
      const departure = this.endpoint_city(segment && segment.departure);
      const arrival = this.endpoint_city(segment && segment.arrival);
      return departure && arrival ? `${departure} - ${arrival}` : "";
    },
    arePassengersSeatedOnSegment(segmentId) {
      return are_passengers_seated_on_segment(this.parsedSelections, segmentId);
    },
    getPassengerSeat(segmentId, passengerId) {
      return get_passenger_seat(this.parsedSelections, segmentId, passengerId);
    },
    isEveryoneSeated(segmentId) {
      return is_everyone_seated(
        this.parsedSelections,
        segmentId,
        this.sittingPassengers.length,
      );
    },
  },
};
</script>

<style lang="scss">
.seat-map-navigation {
  flex: 1;
  gap: 16px;
  .card-header > .btn {
    padding-left: 0;
    padding-right: 0;
  }
  .card-body {
    padding-left: 27px;
    padding-right: 0;
  }
  .selected-seats {
    padding: 8px 0;
    flex-direction: column;
    gap: 8px;
  }
  .passenger-selection-title {
    font-size: $font-size-md;
  }
  .passenger-cancel-col {
    width: 24px;
    text-align: center;
    flex-shrink: 0;
  }
  .segment-route {
    display: flex;
    flex-direction: column;
    &__cities {
      font-size: $font-size-md;
      font-weight: $font-weight-normal;
    }
    .segment-info {
      color: $gray-500;
      font-size: $font-size-md;
      font-weight: $font-weight-normal;
    }
  }
  &--desktop {
    display: none;
    flex-direction: row;
    justify-content: space-between;
    gap: $grid-gutter-width / 3;
    @include media-breakpoint-up(xl) {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
    }
  }
}
</style>
